/* eslint-disable ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/require-tagless-components, no-restricted-properties, no-unused-vars, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
// Ember imports
import config from '@linc-technologies/linced-common/utils/config';
import { alias } from '@ember/object/computed';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// External imports
import SnapshotPdfType from '@linc-technologies/linced-common/enums/snapshot-pdf-type';
import { openSnapshotPdf } from '@linc-technologies/linced-common/utils/snapshots';
import { pollLongRunningTask } from '@linc-technologies/linced-common/utils/long-tasks';

export default class GenerateReport extends Component {
  SnapshotPdfType = SnapshotPdfType;

  // Services
  @service
  fetch;

  @service
  media;

  @service
  session;

  @service
  store;

  @service
  toaster;

  // Attributes
  snapshot = null;

  // snapshotSet is the set we're re-generating reports for.
  // needed to update the set once the report is generated. needs a special function
  @tracked
  snapshotSet = null;

  // schoolId needs to be passed in for the caregiver app because we cannot trust the service there
  @alias('session.activeSchool')
  schoolId;

  openOnFinish = true;
  missingCounter = 1;

  // stealthMode indicates whether the component is in a "stealth" mode, automatically
  // re-generating things without users knowing about it
  // genius idea for this component I had while brushing my teeth
  // true hands-off experience, unless it errors out
  stealthMode = false;

  // Report generation
  reportType = SnapshotPdfType.BASIC;

  @tracked
  generatingReport = false;
  canGenerate = false;
  @tracked
  erroredOut = false;

  // Hooks
  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    if (isEmpty(this.snapshot) && isEmpty(this.snapshotSet)) {
      return;
    }
    if (this.stealthMode) {
      this.generateReport();
    }
  }

  // Functions
  async generateSingleReport() {
    let url = `${config.APP.apiHost}/snapshots/${config.APP.apiPath}/pdf/:generate`;
    let data = {
      schoolId: this.schoolId,
      snapshotId: this.snapshot.id,
      type: this.reportType,
    };

    let response = await this.fetch.post(url, { data });
    let pollingResult = await pollLongRunningTask(
      response.processMonitorId,
      this.store,
    );

    if (this.stealthMode) {
      // Quietly push to the snapshot
      this.addReportToSnapshot(this.snapshot, pollingResult.firstObject);
      return;
    }

    // Default - find a report and open it right away once it's generated
    let pdfReport = await this.store.findRecord(
      'media',
      pollingResult.firstObject,
    );

    openSnapshotPdf(pdfReport);
    this.addReportToSnapshot(this.snapshot, pdfReport);
    return pdfReport;
  }

  async generateSingleReportStealthly() {
    const attempts = 2;

    for (let i = 0; i < attempts; i++) {
      try {
        await this.generateSingleReport();
        break;
      } catch (error) {
        if (i === attempts - 1) {
          this.erroredOut = true;
          throw error; // Only throw after final attempt
        }

        // Wait a bit before retrying (optional)
        await new Promise((res) => setTimeout(res, 2000)); // 2-second wait
      }
    }
  }

  async generateReportsForSet() {
    let url = `${config.APP.apiHost}/snapshots/${config.APP.apiPath}/pdf/:generate`;
    let data = {
      schoolId: this.schoolId,
      setId: this.snapshotSet.id,
      type: this.reportType,
      generateOnlyIfMissing: true,
    };

    let response = await this.fetch.post(url, { data });
    if (this.missingCounter > 200) {
      // Too many to poll for - just make the user refresh the page
      if (response.status === 200) {
        this.toaster.success(
          'Re-generation process has started. It might take longer than 10 minutes. Refresh the page later to check on the progress.',
        );
        return;
      }
    }

    let pollingResult = await pollLongRunningTask(
      response.processMonitorId,
      this.store,
      600000,
    );
    if (pollingResult.length > 0) {
      // At least one report got generated - worth doing something with the set now
      let updatedSet = await this.store.findRecord(
        'snapshots/snapshot-set',
        this.snapshotSet.id,
      );
      this.snapshotSet = updatedSet;
      this.toaster.success(
        'Re-generation process is successful. Please check results in the table below.',
      );
    }
  }

  // addReportToSnapshot adds the newly generated PDF report to the snapshot
  // until the model gets refreshed
  // this is a default function that can be overwritten
  addReportToSnapshot(snapshot, reportId) {
    let newPdf = this.store.createFragment('snapshots/pdf', {
      mediaId: reportId,
    });

    snapshot.pdfs.pushObject(newPdf);
  }

  @action
  async generateReport() {
    this.generatingReport = true;

    try {
      if (!isEmpty(this.snapshotSet)) {
        await this.generateReportsForSet();
      }

      if (!isEmpty(this.snapshot)) {
        if (this.stealthMode) {
          await this.generateSingleReportStealthly();
        } else {
          await this.generateSingleReport();
        }
      }
    } catch (error) {
      this.toaster.error(
        'Error generating report. Please refresh the page and try again.',
      );
    } finally {
      this.generatingReport = false;
    }
  }
}
